import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import {
	AGENDA_LIST_GENERIC_ACTIVITY_LIST_SORT,
	AGENDA_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/appointmentModuleSearch/localStorageKeys';
import createAppointmentModuleSearchState from 'src/stores/appointmentModuleSearch/state';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';

import { FilterListItem, FilterListItemRunType } from 'src/models/filterList';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';

import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetTheAllCalendarModuleAppointmentsForTheUserCallBack,
	GetTheMineCalendarModuleAppointmentsForTheUserCallBack,
} from './callbacks';
import {
	CalendarSearchListFetchPayload,
	CalendarSearchSetListPayload,
	CompleteListExportFetchPayload,
	SetDateTimeRangeFilterPayload,
	SetPaginationPagePayload,
	SetSortPayload,
} from './interfaces';
import { GetTheCalendarModuleAppointmentsForTheUserQuery } from './queries';

const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const paginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: 100,
});

const defineAppointmentModuleSearchStore = (id: string) =>
	defineStore(id, {
		actions: {
			async fetchAllResults(payload: CalendarSearchListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined || payload.searchTerm.trim() === '') {
					this.setAllResults({ page: 1, payload: [], totalCount: 0 });
					return;
				}

				if (payload.clearResultsBeforeFetch) {
					this.resultsAllSliced.clear();
					this.setPaginationPageAction({ activityListType: ActivityListTypeEnum.ALL, page: 1 });
				}

				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = new GetTheCalendarModuleAppointmentsForTheUserQuery(
					'all',
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPageAction({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllCalendarModuleAppointmentsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<FilterListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: FilterListItemRunType,
				});
			},
			async fetchMineResults(payload: CalendarSearchListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined || payload.searchTerm.trim() === '') {
					this.setMineResults({ page: 1, payload: [], totalCount: 0 });
					return;
				}

				if (payload.clearResultsBeforeFetch) {
					this.resultsMineSliced.clear();
					this.setPaginationPageAction({ activityListType: ActivityListTypeEnum.MINE, page: 1 });
				}

				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = new GetTheCalendarModuleAppointmentsForTheUserQuery(
					'mine',
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPageAction({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineCalendarModuleAppointmentsForTheUserCallBack(
					this,
					page,
					queryIdentifier
				);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<FilterListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: FilterListItemRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<FilterListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.resultsAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.resultsMineTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new GetTheCalendarModuleAppointmentsForTheUserQuery(
						completeListExportViewFor(payload.activityListType) as 'all' | 'mine',
						payload.searchTerm,
						payload.hiddenSearchTerm,
						pagination,
						this.sortFor(payload.activityListType)
					)
				);

				let completeList: FilterListItem[] | null = null;

				await payload.queryStore.query<FilterListItem[]>({
					callback: new ResolveListForExportCallback<FilterListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: FilterListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			reset(): void {
				Object.assign(this, createAppointmentModuleSearchState());
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllResults(payload: CalendarSearchSetListPayload): void {
				this.resultsAllTotalCount = payload.totalCount;
				this.resultsAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.resultsAll = dedupByAggregateId(
					[...this.resultsAllSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<FilterListItem>(sort.property as keyof FilterListItem, sort.direction));
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.dateTimeRangeFilter = payload.dateTimeRangeFilter;
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'appointmentModuleSearch') {
					localStorage.setItem(AGENDA_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineResults(payload: CalendarSearchSetListPayload): void {
				this.resultsMineTotalCount = payload.totalCount;
				this.resultsMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.resultsMine = dedupByAggregateId(
					[...this.resultsMineSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<FilterListItem>(sort.property as keyof FilterListItem, sort.direction));
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setSortAction(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
				if (this.$id === 'appointmentModuleSearch') {
					localStorage.setItem(AGENDA_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? paginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createAppointmentModuleSearchState,
	});

export const useAppointmentModuleSearchStore = defineAppointmentModuleSearchStore('appointmentModuleSearch');

const appointmentModuleSearchInstanceDefinitions = new Map<
	string,
	ReturnType<typeof defineAppointmentModuleSearchStore>
>();

// Divergenz (Task 2.8, siehe Report): der agendaList-Route-Name ist NICHT die reale defineStore-Id
// dieses Stores ('appointmentModuleSearch') — anders als bei allen bisherigen Modulen, wo Route-Name
// und Store-Id identisch waren. deriveListStoreId() wird hier bewusst mit der STORE-eigenen Id
// aufgerufen ('appointmentModuleSearch#list:X'), NICHT mit dem Route-Namen 'agendaList'. Das
// genericListIdentifier/Tab-Namespace ('agendaList#list:X') lebt getrennt davon in
// useListInstanceLifecycle (Route-Name-basiert) — beide Namespaces bleiben unabhängig voneinander.
export const createAppointmentModuleSearchStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useAppointmentModuleSearchStore();
	}

	const storeId = deriveListStoreId('appointmentModuleSearch', instanceId);
	let definition = appointmentModuleSearchInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineAppointmentModuleSearchStore(storeId);
		appointmentModuleSearchInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
